import { useEffect } from 'react';
import {
  Box,
  Container,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';

/**
 * Contact page — intentionally form-free. Visitors reach out directly by
 * email or phone; the page just surfaces the channels. WinorGroup, the
 * publisher of AfroEspace, is the point of contact.
 */
const EMAIL = 'info@winorgroup.com';
/** Display form of the phone number; the tel: link strips the spaces. */
const PHONE_DISPLAY = '+49 15567 301702';
const PHONE_HREF = `tel:${PHONE_DISPLAY.replace(/\s+/g, '')}`;

export function ContactPage() {
  useEffect(() => {
    document.title = 'Contact — AfroEspace';
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
      >
        Contact
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 1.5 }}>
        Contactez-nous
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
        Une question, une suggestion ou besoin d&apos;aide ? Écrivez-nous ou
        appelez-nous directement — nous vous répondrons dans les meilleurs
        délais.
      </Typography>

      <Stack spacing={2}>
        <ContactCard
          icon={<EmailRoundedIcon />}
          label="Email"
          value={EMAIL}
          href={`mailto:${EMAIL}`}
        />
        <ContactCard
          icon={<PhoneRoundedIcon />}
          label="Téléphone"
          value={PHONE_DISPLAY}
          href={PHONE_HREF}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mt: 4, color: 'text.secondary' }}
      >
        <PlaceRoundedIcon sx={{ fontSize: '1.1rem' }} />
        <Typography variant="body2">
          WinorGroup, éditeur d&apos;AfroEspace — Dakar, Sénégal
        </Typography>
      </Stack>
    </Container>
  );
}

function ContactCard({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'border-color 150ms ease',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? `${theme.palette.primary.main}22`
                : `${theme.palette.primary.main}14`,
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.1em', fontWeight: 700, display: 'block' }}
          >
            {label}
          </Typography>
          <Link
            href={href}
            sx={{ color: 'primary.main', fontWeight: 600, fontSize: '1.0625rem' }}
          >
            {value}
          </Link>
        </Box>
      </Stack>
    </Paper>
  );
}
